import React from "react";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { store, persistor } from "./store";
import "./App.css";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { isPlatformSessionMode } from "./auth/mode";
import RequireAuth from "./auth/RequireAuth";
import Login from "./routes/Login";
import Callback from "./routes/Callback";
import AppShell from "./routes/AppShell";
import RouteBoundary from "./components/RouteBoundary";
import WizardHeader from "./components/home/WizardHeader";
import NavBar from "./components/NavBar";
import { routes } from "./config/routes";
import RouteWrapper from "./components/home/RouteWrapper";
import HomeLayout from "./components/HomeLayout";
import { ConfigProvider, Spin } from "antd";
import { colors } from "./config/constants";
import { useSelector } from "react-redux";

// Platform session mode layout component
const PlatformSessionLayout = ({ children }) => {
  const sidebarOpen = useSelector(
    (state) => state.navigation?.sidebarOpen ?? true,
  );

  return (
    <div className="flex h-[calc(100vh-64px)]">
      {/* Sidebar */}
      <div
        className={`${
          sidebarOpen ? "w-64" : "w-0"
        } flex-shrink-0 transition-all duration-300 ease-in-out overflow-hidden`}
      >
        {sidebarOpen && <NavBar />}
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-auto">
        <div className="flex-1 px-4 sm:px-6 lg:px-8 py-2">{children}</div>
      </div>
    </div>
  );
};

function App() {
  return (
    <Provider store={store}>
      <PersistGate loading={<Spin fullscreen />} persistor={persistor}>
        <ConfigProvider
          theme={{
            token: {
              colorPrimaryBg: colors.cyan1,
              colorPrimaryHoverBg: colors.cyan2,
              colorPrimaryBorder: colors.cyan3,
              colorPrimaryBorderHover: colors.cyan4,
              colorPrimaryHover: colors.cyan6,
              colorPrimary: colors.cyan7,
              colorPrimaryActive: colors.cyan8,
              colorPrimaryTextHover: colors.cyan6,
              colorPrimaryText: colors.cyan7,
              colorPrimaryTextActive: colors.cyan8,
              fontFamily: "Poppins",
              // fontSize: 11,

              colorText: colors.ColorsBaseCyan10,
              colorTextSecondary: colors.ColorsNeutralTextcolorTextSecondary,

              colorBgSpotlight: colors.black85, // Only used for tooltip background

              colorLink: colors.cyan7,
              colorLinkHover: colors.cyan4,
              colorLinkActive: colors.cyan8,

              colorBorderSecondary: colors.grey,

              motionDurationFast: "0.1s",
              motionDurationMiddle: "0.9s",
              motionDurationSlow: "0.1s",
              motion: true,
            },
            components: {
              Button: {
                defaultColor: colors.cyan7,
                defaultBorderColor: colors.cyan7,
                primaryShadow: `0 1px 2px rgba(5, 255, 215, 0.1)`,
              },
              Typography: {
                titleMarginTop: 0,
                titleMarginBottom: 0,
              },
              Menu: {
                collapsedWidth: 62,
                iconSize: 16,
                itemSelectedBg: colors.cyan7,
                itemSelectedColor: colors.white,
                itemHoverBg: colors.black15,
                itemBorderRadius: 100,
                controlItemBgActive: colors.cyan7,
                colorBgContainer: colors.grey2,
                itemMarginInline: 10,
                activeBarBorderWidth: 0,
              },
              Table: {
                // cellPaddingBlock: 4,
                // cellPaddingBlockSM: 4,
              },
              Layout: {
                bodyBg: colors.white,
                headerBg: colors.white,
              },
            },
          }}
        >
          {isPlatformSessionMode() ? (
            <Router>
              <div className="App min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
                <WizardHeader />
                <PlatformSessionLayout>
                  <Routes>
                    <Route
                      path="/"
                      element={
                        <RouteBoundary>
                          <RouteWrapper creating={false} />
                        </RouteBoundary>
                      }
                    />
                    <Route
                      path="/create"
                      element={
                        <RouteBoundary>
                          <RouteWrapper creating={true} />
                        </RouteBoundary>
                      }
                    />
                    {routes.map((r) => (
                      <Route key={r.path} path={r.path} element={r.element} />
                    ))}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </PlatformSessionLayout>
              </div>
            </Router>
          ) : (
            <Router>
              <div className="App min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
                <div>
                  <WizardHeader />
                  {/* Routes */}
                  <Routes>
                    {/* Public routes */}
                    <Route exact path="/login" element={<Login />} />
                    <Route path="/callback" element={<Callback />} />
                    <Route path="/azure/callback" element={<Callback />} />

                    {/* Existing app routes (protected) */}
                    <Route element={<RequireAuth />}>
                      <Route
                        path="/*"
                        element={
                          <HomeLayout>
                            <Routes>
                              <Route
                                path="/"
                                element={
                                  <RouteBoundary>
                                    <RouteWrapper creating={false} />
                                  </RouteBoundary>
                                }
                              />
                              <Route
                                path="/create"
                                element={
                                  <RouteBoundary>
                                    <RouteWrapper creating={true} />
                                  </RouteBoundary>
                                }
                              />
                              {routes.map((r) => (
                                <Route
                                  key={r.path}
                                  path={r.path}
                                  element={r.element}
                                />
                              ))}
                              {/* Demo protected app shell */}
                              <Route path="/app" element={<AppShell />} />
                            </Routes>
                          </HomeLayout>
                        }
                      />
                    </Route>

                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </div>
              </div>
            </Router>
          )}
        </ConfigProvider>
      </PersistGate>
    </Provider>
  );
}

export default App;
